import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Camera,
  Upload,
  ArrowRight,
  ChevronLeft,
  ImageIcon,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import Header from "../components/Header";
import ContourVibePicker from "../components/ContourVibePicker";
import FirstRoutineNudge from "../components/FirstRoutineNudge";
import { useSession } from "../context/SessionContext";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { trackFP } from "../lib/firstPartyAnalytics";
import { checkImageQuality } from "../lib/imageQuality";
import DemoPrivacyNotice from "../components/DemoPrivacyNotice";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result; // data:mime;base64,....
      const [meta, data] = res.split(",");
      const mime = /:(.*?);/.exec(meta)?.[1] || "image/jpeg";
      resolve({ base64: data, mime, previewUrl: res });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Capture = ({ ephemeral = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    photo, setPhoto, preferences, setPreferences,
    inspiration, setInspiration,
  } = useSession();
  const [mode, setMode] = useState("upload"); // "camera" | "upload"
  const [streamReady, setStreamReady] = useState(false);
  const [capturing, setCapturing] = useState(false);

  // Pick up the inspiration brief if the user came in via /recreate.
  // Router state is one-shot; we move it into SessionContext so it
  // survives any in-page navigation and is automatically picked up
  // by Analyzing on submit.
  useEffect(() => {
    const incoming = location.state?.inspiration;
    if (incoming && !inspiration) {
      setInspiration(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const videoRef = useRef(null);
  // Quality pre-check state — runs on every new photo before the user
  // can hit Continue. `null` while idle, "checking" while running.
  const [quality, setQuality] = useState(null);

  // Fire capture_started exactly once on mount. We don't track every mode
  // switch; that's noise. Mode is captured at the moment a photo is taken.
  useEffect(() => {
    track("capture_started");
  }, []);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Whenever the user produces a NEW photo, run the quality pre-check.
  // We block "Continue" on hard errors (no face, dark, blurry) and show
  // a soft warning for borderline cases. Saves LLM spend on bad inputs.
  useEffect(() => {
    let cancelled = false;
    if (!photo) {
      setQuality(null);
      return;
    }
    setQuality({ status: "checking" });
    (async () => {
      try {
        const report = await checkImageQuality({
          base64: photo.base64,
          mime: photo.mime,
        });
        if (cancelled) return;
        setQuality({ status: "done", ...report });
        track("capture_quality_checked", {
          ok: report.ok,
          face_count: report.faceCount,
          brightness: report.brightness,
          blur_variance: report.blurVariance,
          issues: report.issues.map((i) => i.code),
        });
      } catch (e) {
        if (cancelled) return;
        // Fail-open: if the check itself crashed, still let the user
        // continue. Backend has its own validation.
        setQuality({ status: "done", ok: true, issues: [], failedToRun: true });
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[capture] quality pre-check failed:", e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [photo]);

  // Acquire camera stream when in camera mode. The stream is parked
  // in streamRef and a render tick is forced via `streamTick` so the
  // attach-effect below can wire it onto the <video> element as soon
  // as the element exists in the DOM. This split fixes a race where
  // getUserMedia resolves BEFORE the <video> mounts (happens on
  // revisits with pre-granted permission + AnimatePresence delaying
  // the camera-pane mount): the previous single-effect code's
  // `if (videoRef.current)` check would silently no-op and the
  // "Waking your camera…" overlay would stay forever.
  const [streamTick, setStreamTick] = useState(0);
  useEffect(() => {
    if (mode !== "camera" || photo) return;
    let cancelled = false;
    setStreamReady(false);
    let acquired = null;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 900 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        acquired = stream;
        streamRef.current = stream;
        setStreamTick((n) => n + 1); // notify attach-effect
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[capture] camera unavailable:", e?.message || e);
        }
        toast.error(
          "Camera access denied or unavailable. You can upload a photo instead."
        );
        setMode("upload");
      }
    })();
    return () => {
      cancelled = true;
      if (acquired) {
        acquired.getTracks().forEach((t) => t.stop());
      }
      if (streamRef.current === acquired) {
        streamRef.current = null;
      }
    };
  }, [mode, photo]);

  // Attach the acquired stream to the <video> element. Runs whenever
  // either the stream arrives (streamTick bumps) OR the video element
  // is (re)mounted via the callback ref below — covering both
  // orderings. Idempotent: bails out if already attached.
  useEffect(() => {
    if (mode !== "camera" || photo) return;
    const v = videoRef.current;
    const s = streamRef.current;
    if (!v || !s) return;
    if (v.srcObject === s) return;
    v.srcObject = s;
    const p = v.play();
    if (p && typeof p.then === "function") {
      p.then(() => setStreamReady(true)).catch((err) => {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[capture] video.play failed:", err?.message || err);
        }
        // Still surface the frame — autoplay errors don't mean the
        // stream is dead. User can tap to play if needed.
        setStreamReady(true);
      });
    } else {
      setStreamReady(true);
    }
  }, [mode, photo, streamTick]);

  // Callback ref — stable identity via useCallback so React only
  // invokes it on actual mount/unmount of the <video> element (not on
  // every render — passing a fresh inline function would cause React
  // to detach+reattach on every commit, and the streamTick bump in
  // the body would create an infinite re-render loop). The
  // node-identity early-return is a second line of defence: if React
  // ever calls us with the same node, we no-op.
  const setVideoRef = useCallback((node) => {
    if (videoRef.current === node) return;
    videoRef.current = node;
    if (node && streamRef.current) {
      setStreamTick((n) => n + 1);
    }
  }, []);

  const takePhoto = async () => {
    if (!videoRef.current) return;
    setCapturing(true);
    // small delay so shutter feels calm
    await new Promise((r) => setTimeout(r, 350));
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    const base64 = dataUrl.split(",")[1];
    setPhoto({ base64, mime: "image/jpeg", previewUrl: dataUrl });
    track("capture_photo_taken", { mode: "camera" });
    // First-party funnel: selfie_upload_started (camera path)
    trackFP("selfie_upload_started", { mode: "camera" });
    trackFP("selfie_upload_success", { mode: "camera" });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    setCapturing(false);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|png|webp)/.test(file.type)) {
      toast.error(t("capture.errInvalidType"));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("capture.errTooLarge"));
      return;
    }
    // First-party funnel: fire BEFORE the FileReader work so we capture
    // intent even if the read fails. The success event below fires only
    // after setPhoto, which guarantees the upload + decode worked.
    trackFP("selfie_upload_started", { mode: "upload" });
    const result = await fileToBase64(file);
    setPhoto(result);
    track("capture_photo_uploaded", { mode: "upload" });
    trackFP("selfie_upload_success", { mode: "upload" });
  };

  const retake = () => {
    track("capture_retake");
    setPhoto(null);
  };

  const proceed = () => {
    if (!photo) return;
    // Block on hard errors (no face / too dark / too blurry). Warnings
    // don't block — user may know better than us.
    if (quality?.status === "done" && quality.ok === false) {
      toast.error(t("capture.qualityFixAbove"));
      return;
    }
    // Canonical Feb 2026 conversion-funnel event (Step 6). Fires the
    // moment the selfie has cleared quality gates and the user has
    // committed to proceed. The vision-pass starts in /analyzing; this
    // event is the "image acquired & ready for analysis" marker.
    track("selfie_completed", { source: mode });
    trackFP("selfie_completed", { source: mode });
    navigate(ephemeral ? "/demo/analyzing" : "/analyzing");
  };

  const continueDisabled =
    !photo ||
    quality?.status === "checking" ||
    (quality?.status === "done" && quality.ok === false);

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />

      {ephemeral && (
        <div className="pt-20 px-4 sm:px-6">
          <DemoPrivacyNotice />
        </div>
      )}

      <section className={`${ephemeral ? "pt-8" : "pt-24 sm:pt-32"} pb-20 px-4 sm:px-6`} data-testid="capture-root">
        <div className="max-w-3xl mx-auto">
          {/* First-routine activation nudge — renders only for free
              users ≥24h post-signup with zero routines. Backend decides
              eligibility; this is a no-op for everyone else. */}
          {!ephemeral && !photo && (
            <div className="mb-6">
              <FirstRoutineNudge variant="capture" />
            </div>
          )}

          {/* Inspiration mode banner — when the user came here from
              /recreate, we show what we're recreating so they know
              their selfie will be adapted to that specific look. */}
          {inspiration?.brief && (
            <div
              className="mb-6 rounded-2xl border border-[#E4D2DC] bg-gradient-to-br from-[#FBF4F2] via-[#F4E5E1] to-[#EFD8D2] p-4 sm:p-5"
              data-testid="capture-inspiration-banner"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-1">
                Recreating
              </div>
              <div className="text-[#2C2123] font-medium leading-snug">
                {inspiration.brief.look_name || "Your inspiration look"}
              </div>
              {inspiration.brief.vibe && (
                <div className="text-sm text-[#5C4B51] mt-1 leading-snug">
                  {inspiration.brief.vibe}
                </div>
              )}
              <button
                type="button"
                onClick={() => setInspiration(null)}
                className="text-xs text-[#9B6A6C] hover:text-[#5D4050] underline-offset-4 hover:underline mt-2 transition-colors"
                data-testid="capture-inspiration-clear"
              >
                Clear inspiration
              </button>
            </div>
          )}

          <div className="mb-8 sm:mb-10 text-center">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-3">
              {t("capture.kicker")}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2C2123] leading-tight">
              {t("capture.headline")} <span className="font-serif italic text-[#5D4050]">{t("capture.headlineGlow")}</span>{t("capture.headlineDot")}
            </h2>
            <p className="text-[14px] sm:text-base text-[#5C4B51] mt-3 max-w-md mx-auto leading-relaxed">
              {t("capture.subline")}
            </p>
          </div>

          {/* Mode toggle */}
          {!photo && (
            <div className="flex items-center justify-center mb-8">
              <div className="inline-flex p-1 rounded-full bg-white border border-[#EADDD7]">
                <button
                  className={`px-5 py-2 rounded-full text-sm transition-all ${
                    mode === "camera"
                      ? "bg-[#9B6A6C] text-white shadow"
                      : "text-[#5C4B51]"
                  }`}
                  onClick={() => setMode("camera")}
                  data-testid="capture-toggle-camera"
                >
                  <span className="inline-flex items-center gap-2">
                    <Camera className="h-4 w-4" /> {t("capture.useCamera")}
                  </span>
                </button>
                <button
                  className={`px-5 py-2 rounded-full text-sm transition-all ${
                    mode === "upload"
                      ? "bg-[#9B6A6C] text-white shadow"
                      : "text-[#5C4B51]"
                  }`}
                  onClick={() => setMode("upload")}
                  data-testid="capture-toggle-upload"
                >
                  <span className="inline-flex items-center gap-2">
                    <Upload className="h-4 w-4" /> {t("capture.uploadPhoto")}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Big capture frame — only shown BEFORE a photo is taken.
              Once we have a photo, the frame collapses to a small
              thumbnail (further down) so the contour question takes
              the visual lead instead of getting buried under a giant
              selfie preview. */}
          {!photo && (
            <div className="relative mx-auto max-w-md" data-testid="capture-frame">
              <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-[#F5EFEB] soft-shadow-lg border border-[#EADDD7]">
                <AnimatePresence initial={false}>
                  {mode === "camera" ? (
                    <motion.div
                      key="camera"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="w-full h-full"
                    >
                      <video
                        ref={setVideoRef}
                        muted
                        playsInline
                        autoPlay
                        className="w-full h-full object-cover"
                        style={{ transform: "scaleX(-1)" }}
                        data-testid="capture-video"
                      />
                      {!streamReady && (
                        <div className="absolute inset-0 flex items-center justify-center text-[#8A7D82]">
                          <span className="text-sm">{t("capture.wakingCamera")}</span>
                        </div>
                      )}

                      {/* In-frame shutter button — no need to scroll */}
                      {streamReady && (
                        <button
                          onClick={takePhoto}
                          disabled={capturing}
                          className="group absolute left-1/2 -translate-x-1/2 bottom-6 h-[72px] w-[72px] rounded-full bg-white/15 backdrop-blur-md border-[3px] border-white/85 hover:border-white shadow-[0_8px_30px_rgba(0,0,0,0.35)] hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all"
                          data-testid="capture-take-photo-frame-btn"
                          aria-label={t("capture.takePhotoAria")}
                        >
                          <span className="h-[54px] w-[54px] rounded-full bg-white group-hover:bg-[#FFF6F2] group-active:bg-[#F2E8E9] transition-colors" />
                        </button>
                      )}
                    </motion.div>
                  ) : (
                    <motion.button
                      key="upload"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-full flex flex-col items-center justify-center gap-4 text-[#8A7D82] hover:text-[#9B6A6C] transition-colors"
                      data-testid="capture-upload-drop"
                    >
                      <div className="h-16 w-16 rounded-full bg-white/70 border border-[#EADDD7] flex items-center justify-center">
                        <ImageIcon className="h-7 w-7" />
                      </div>
                      <div className="text-center">
                        <div className="text-base font-medium text-[#5D4050]">
                          {t("capture.choosePhoto")}
                        </div>
                        <div className="text-sm mt-1">
                          {t("capture.fileTypes")}
                        </div>
                      </div>
                    </motion.button>
                  )}
                </AnimatePresence>

                {/* Soft scan corners — only meaningful pre-capture */}
                <div className="scan-corner tl soft-pulse" />
                <div className="scan-corner tr soft-pulse" />
                <div className="scan-corner bl soft-pulse" />
                <div className="scan-corner br soft-pulse" />

                {/* Calm shutter overlay during capture */}
                {capturing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 bg-white"
                  />
                )}
              </div>
            </div>
          )}

          {/* Compact after-capture row — small thumbnail + a "retake"
              affordance, leaving room for the contour question and
              the quality panel to be the visual focus below. */}
          {photo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className="flex items-center justify-center gap-4"
              data-testid="capture-thumb-row"
            >
              <div className="h-20 w-16 rounded-2xl overflow-hidden bg-[#F5EFEB] border border-[#EADDD7] soft-shadow shrink-0">
                <img
                  src={photo.previewUrl}
                  alt={t("capture.selfiePreviewAlt")}
                  className="w-full h-full object-cover"
                  data-testid="capture-preview-img"
                />
              </div>
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-[#9B6A6C] font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {t("capture.inFrame")}
                </div>
                <button
                  onClick={retake}
                  className="block mt-1 text-sm text-[#5D4050] hover:text-[#9B6A6C] underline underline-offset-2"
                  data-testid="capture-retake-inline-btn"
                >
                  {t("capture.retakeUpload")}
                </button>
              </div>
            </motion.div>
          )}

          {/* Hidden file input — always rendered so retake → upload works */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onUpload}
            data-testid="capture-upload-input"
          />

          {/* Quality pre-check feedback — only when a photo is present */}
          {photo && quality && (
            <div className="max-w-md mx-auto mt-5" data-testid="capture-quality-panel">
              {quality.status === "checking" && (
                <div
                  className="flex items-center gap-3 bg-white border border-[#EADDD7] rounded-2xl px-4 py-3 text-sm text-[#5C4B51] soft-shadow"
                  data-testid="capture-quality-checking"
                >
                  <Loader2 className="h-4 w-4 animate-spin text-[#9B6A6C]" />
                  {t("capture.qualityChecking")}
                </div>
              )}
              {quality.status === "done" && quality.issues?.length === 0 && !quality.failedToRun && (
                <div
                  className="flex items-center gap-3 bg-[#EFF6EE] border border-[#CFE5C9] rounded-2xl px-4 py-3 text-sm text-[#3A5A36]"
                  data-testid="capture-quality-pass"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  {t("capture.qualityPass")}
                </div>
              )}
              {quality.status === "done" && quality.issues?.length > 0 && (
                <ul
                  className="space-y-2"
                  data-testid="capture-quality-issues"
                >
                  {quality.issues.map((issue) => {
                    const isError = issue.severity === "error";
                    return (
                      <li
                        key={issue.code}
                        className={`flex items-start gap-3 rounded-2xl px-4 py-3 text-sm border ${
                          isError
                            ? "bg-[#F8EEEE] border-[#E5C5C2] text-[#5D4050]"
                            : "bg-[#FBF5EE] border-[#E8D8C2] text-[#5C4B51]"
                        }`}
                        data-testid={`capture-quality-${issue.code}`}
                      >
                        <AlertCircle
                          className={`h-4 w-4 mt-0.5 shrink-0 ${
                            isError ? "text-[#9B6A6C]" : "text-[#B58F4A]"
                          }`}
                        />
                        <span>{issue.message}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Contour vibe — only surfaces once a photo is in hand,
              so the question feels like a natural "before we go" moment
              rather than a survey gating the upload step. */}
          {photo && (
            <div className="mt-8 max-w-xl mx-auto">
              <ContourVibePicker
                value={preferences.contour_choice}
                onChange={(next) =>
                  setPreferences((prev) => ({ ...prev, contour_choice: next }))
                }
              />
            </div>
          )}

          {/* Actions */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            {!photo && mode === "upload" && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-8 py-4 transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
                data-testid="capture-pick-file-btn"
              >
                <Upload className="h-4 w-4" />
                {t("capture.chooseFile")}
              </button>
            )}

            {photo && (
              <button
                onClick={proceed}
                disabled={continueDisabled}
                className={`inline-flex items-center gap-2 rounded-full px-8 py-3 transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] ${
                  continueDisabled
                    ? "bg-[#D5A8A4]/60 text-white cursor-not-allowed"
                    : "bg-[#9B6A6C] text-white hover:bg-[#805557] hover:-translate-y-0.5"
                }`}
                data-testid="capture-continue-btn"
              >
                {quality?.status === "checking" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("capture.checking")}
                  </>
                ) : (
                  <>
                    {t("capture.continueLabel")}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>

          <div className="mt-8 flex items-center justify-center">
            <button
              onClick={() => navigate(ephemeral ? "/demo/onboarding" : "/onboarding")}
              className="inline-flex items-center gap-1 text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors"
              data-testid="capture-back-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("capture.back")}
            </button>
          </div>

          <p className="text-xs text-center text-[#8A7D82] mt-10 max-w-md mx-auto">
            {t("capture.goldenTip")}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Capture;
